/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { filterType } from '../../config/enum';
import DefaultApiParameters from '../../enum/DefaultApiParameters';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  AccountRevenueSharingInterFace,
  RevenueSharingRequestInterface,
  RevenueSharingResponseInterface,
} from '../../interface/revenue-management.interface';
// import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
// import ConvertUTCtoHHMM from '../../shared-functions/ConvertUTCtoHHMM';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import { removeEmptyKeys } from '../../shared-functions/DateTime';
// import FormatUtcString from '../../shared-functions/FormatUtcString';
import revenueApi from '../../utils/api/revenue';

import { setNotificationMessage } from './NotificationManagementSlice';

interface RevenueSharingInitialProperties {
  revenueList: AccountRevenueSharingInterFace[];
  limit: number;
  page: number;
  totalPages: number;
  totalResults: number;
  loading: boolean;
  filters: CommonFilterProperties[];
  filterListLoading: boolean;
  isFilterUpdated: boolean;
  sortBy: string;
  sortField: string;
}

// a thunk to fetch accounts
export const fetchAccountRevenueSharingList = createAsyncThunk<
  RevenueSharingResponseInterface,
  RevenueSharingRequestInterface
>(
  'revenue-account-sharing-account/fetchAccountRevenueSharingList',
  async (data: RevenueSharingRequestInterface, { dispatch }) => {
    try {
      const response = await revenueApi.fetchRevenueSharing(
        removeEmptyKeys({ ...data, groupBy: 'account' }),
      );
      return response.data;
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          type: 'error',
          message: error?.response?.data?.message,
          code: error?.response?.data?.code,
          status: true,
        }),
      );
      throw error;
    }
  },
);

// a thunk to fetch devices filters
export const fetchListAccountRevenueSharingFilters = createAsyncThunk<
  CommonFilterProperties[], // Return type
  void // No need for an argument, since we are not using the payload
>('payment-management/fetchListAccountRevenueSharingFilters', async (_, { dispatch }) => {
  try {
    const response = await revenueApi.fetchRevenueSharingListFilters();
    const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response.data);
    return updatedResponse;
  } catch (error: any) {
    dispatch({
      type: 'notification/setNotificationDetails',
      payload: {
        type: 'error',
        notificationDetails: {
          statusCode: error?.response?.data?.code || 200,
          message: error?.response?.data?.message || 'Something went wrong!',
        },
      },
    });
    throw error;
  }
});

// Apply default filters
const today = new Date();
const yesterday = new Date(today);
yesterday.setDate(today.getDate() - 1);

/**
 * Filter date format func
 * @param date
 * @returns
 */
const formatDate = (date: Date): string => date.toISOString().split('T')[0];

const availableFilters: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
  {
    name: 'Date Range',
    key: 'startDate_endDate',
    value: '',
    key1: 'startDate',
    value1: formatDate(yesterday),
    key2: 'endDate',
    value2: formatDate(today),
    type: 'dateRange',
    listOfValue: [],
    applyFilter: true,
    enable: true,
    disableClearFilter: true,
    disableClearFilterMessage: 'Date filter is required!',
    FEAddedFilter: true, // This is used to identify if the filter is added from FE
  },
];

// Initial state
const initialState: RevenueSharingInitialProperties = {
  revenueList: [],
  limit: DefaultApiParameters.limit,
  page: DefaultApiParameters.page,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  filters: availableFilters,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: '',
  sortField: '',
};

// Create the slice
export const accountRevenueSharingSlice = createSlice({
  name: 'revenue-account-sharing-account',
  initialState,
  reducers: {
    /**
     * Update device management filters
     * @param state
     * @param action
     */
    updateAccountRevenueSharingFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
      }
    },
    /**
     * Function to change isFiltersAppliedStatus
     * @param state
     */
    disableAccountRevenueSharingApplied: (state) => {
      state.isFilterUpdated = false;
    },
    /**
     * function for set sortBy
     * @param state
     * @param action
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
  },
  /**
   * Use the extra reducers function
   * @param builder
   */
  extraReducers: (builder) => {
    builder
      .addCase(fetchAccountRevenueSharingList.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAccountRevenueSharingList.fulfilled, (state, action) => {
        state.loading = false;
        state.revenueList = action.payload?.results?.length
          ? action.payload?.results?.map((result) => ({
              ...result,
              id: result._id,
              totalFoRevenue: `${parseFloat(result?.totalFoRevenue)?.toFixed(2)}`,
              totalAdminRevenue: `${parseFloat(result?.totalAdminRevenue)?.toFixed(2)}`,
            }))
          : [];
        state.limit = action.payload.limit;
        state.page = action.payload.page;
        state.totalPages = action.payload.totalPages;
        state.totalResults = action.payload.totalResults;
      })
      .addCase(fetchAccountRevenueSharingList.rejected, (state) => {
        state.loading = false;
        // Handle error if needed
      });
    builder
      .addCase(fetchListAccountRevenueSharingFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchListAccountRevenueSharingFilters.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          state.filters = [...(state.filters || []), ...action.payload].filter(
            (object, index, self) => index === self.findIndex((t) => t.key === object.key),
          );
        },
      )
      .addCase(fetchListAccountRevenueSharingFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});

// Export the actions
export const {
  updateAccountRevenueSharingFilters,
  setSortData,
  disableAccountRevenueSharingApplied,
} = accountRevenueSharingSlice.actions;

// Export the reducer
export default accountRevenueSharingSlice.reducer;
